// Modules
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import browserHistory from 'react-router/lib/browserHistory';
import cx from 'classnames';
import throttle from 'lodash/throttle';
import get from 'lodash/get';
import moment from 'moment';
// Components
import NoteInput from '../Notes/NoteInput';
import BottomTooltip from '../BottomTooltip/BottomTooltip';
import OMLabelNew from '../OMComponents/OMLabelNew/OMLabelNew';
import ShareTooltip from '../ShareTooltip/ShareTooltip';
import Modal from '../Modal/Modal';
import OMLabelTop from '../OMComponents/OMLabelTop/OMLabelTop';
import LazyImage from '../LazyImage/LazyImage';
// Helpers
import parkingConverter from '../../utils/parkingConverter';
import checkIsForeignAddress from '../../helpers/checkIsForeignAddress';
import { addEnding } from '../../utils/getIconStatus';
import { trackEvent, trackModalOpen } from '../../utils/analytics/analyticsManager';
import appendLocale from '../../helpers/appendLocale';
import { supportEmail, analyticsEventAction, ENTITY_TYPES } from '../../constants';
import { eventLabel, eventAction, eventCategory } from '../../utils/analytics/analyticsConstants';
import getButtonsConfig from '../../pages/ManagePropertiesPage/controlButtonsConfig';
import displayPrice from '../../helpers/displayPrice';
import { getPropertyDetailsPageUrl } from '../../utils/getDetailsPageUrl';
import getPriceForCurrency from '../../utils/getPriceForCurrency';
// Styles
import * as styles from './PropertyCell.scss';
import propertyNoteIcon from '../../../static/icons/note.svg';
import isDescendant from '../../utils/isDescendant';
import makeSharingUrl from '../../utils/makeSharingUrl';
import thumbnailPic from '../../../static/images/images_placeholder.svg';
import modifyImageLink from '../../utils/modifyImageLink';
import buildCardPhotoAlt from '../../utils/seo/properties/buildCardPhotoAlt';
import formatTimeFromDate from '../../helpers/formatTimeFromDate';
import buildVideoLinks from '../../utils/buildVideoLinks';
import {
  isVideoPosterReady, isImagePosterReady, resolveMediaStatusBadge
} from '../../utils/normalizeMediaStatus';
import VideoStatusBadge from '../VideoStatusBadge/VideoStatusBadge';
import ImageStatusBadge from '../ImageStatusBadge/ImageStatusBadge';

class PropertyCell extends Component {
  state = {
    hover: false,
    showNote: false,
    shownPromotedTitle: false,
    isShareTooltipShown: false,
    isModalControlsOpen: false
  };

  componentDidMount() {
    if (this.props.isMobile) {
      this.propertyCell.addEventListener('click', this.handleClick, true);
    }

    if (!this.props.isMobile) {
      this.favoriteIcon && this.favoriteIcon.addEventListener('click', this.toggleFavorites, true);
    }

    if ((this.props.className !== 'inManageProperties' || this.props.isMobile)) {
      this.propertyCell.addEventListener('click', this.openDetailsHandler, true);
    }

    if (this.props.className === 'inManageProperties' && !this.props.isMobile) {
      this.setClickableAreaSize();
      this._resizeHandler = throttle(this.setClickableAreaSize, 66); // Around 15 fps
      window.addEventListener('resize', this._resizeHandler);
    }

    if (this.props.autoDeactivatedProperty
      && this.props.autoDeactivatedProperty === this.props.id
      && this.props.isActive) {
      this.toggleActivity();
    }
  }

  componentWillReceiveProps({ propertyNote, autoDeactivatedProperty }) {
    if (!propertyNote && this.props.propertyNote !== propertyNote) {
      this.setState({ showNote: false });
    }
    if (autoDeactivatedProperty && this.props.autoDeactivatedProperty !== autoDeactivatedProperty) {
      if (autoDeactivatedProperty === this.props.id && this.props.isActive) {
        this.toggleActivity();
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.showNote && this.state.showNote) {
      document.body.addEventListener('click', this.hideNote, true);
    }
    if (prevState.showNote && !this.state.showNote) {
      document.body.removeEventListener('click', this.hideNote, true);
    }
  }

  componentWillUnmount() {
    if (this.props.isMobile) {
      this.propertyCell.removeEventListener('click', this.handleClick, true);
    }
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
    }

    this.propertyCell.removeEventListener('click', this.openDetailsHandler, true);
    this.favoriteIcon && this.favoriteIcon.removeEventListener('click', this.toggleFavorites, true);
  }

  // Uncomment to enable delete property modal
  // onDelete = (evt) => {
  //   evt.preventDefault();
  //   this.props.showDeleteModal(this.props.id);
  // };

  setClickableAreaSize = () => {
    if (this.propertyInfo && this.clickableArea && this.propertyPhoto) {
      let width = this.propertyInfo.offsetWidth + this.propertyPhoto.offsetWidth + 55;

      if (this.propertyRow && this.propertyRow.offsetLeft) {
        width += this.propertyRow.offsetLeft;
      }

      width = `width: ${Math.floor(width * 1.05)}px;`;

      this.clickableArea.setAttribute('style', `${width}`);
    }
  };

  getResolvedPrice = () => {
    const { price, currency, prices, overrideCurrency, addressData } = this.props;
    const isForeign = checkIsForeignAddress(addressData);

    return isForeign
      ? getPriceForCurrency(price, currency, prices, overrideCurrency)
      : { price, currency };
  };

  hideNote = (evt) => {
    const isNoteIcon = this.noteWrapper && isDescendant(this.noteWrapper, evt.target);

    if (!isNoteIcon) {
      this.setState({ showNote: false });
    }
  };

  handleClick = (evt) => {
    const { classList } = evt.target;
    const isPromotedIcon = classList.contains('icon-promoted');
    const isFavoriteIcon = classList.contains('favoriteToggler');
    const isPromotedTitle = classList.contains(styles.promotedTitle);

    if (isFavoriteIcon) {
      this.toggleFavorites(evt);
    }

    if (this.state.shownPromotedTitle && !isPromotedTitle) {
      this.setState({ shownPromotedTitle: false });
      evt.stopPropagation();
    } else if (isPromotedIcon && !this.state.shownPromotedTitle) {
      this.setState({ shownPromotedTitle: true });
      evt.stopPropagation();
    } return true;
  };

  openDetailsHandler = (evt) => {
    const { classList } = evt.target;
    const isFavoriteIcon = classList.contains('favoriteToggler');
    const isNoteIcon = this.noteWrapper && isDescendant(this.noteWrapper, evt.target);
    const isControlButtons = this.controlButtons && isDescendant(this.controlButtons, evt.target);
    const isMoreControlsButton = this.moreControlsButton && (
      isDescendant(this.moreControlsButton, evt.target) || this.moreControlsButton === evt.target
    );

    if (isFavoriteIcon || isNoteIcon || isControlButtons || isMoreControlsButton) {
      return;
    }

    if (!isFavoriteIcon && !isNoteIcon) {
      evt.stopPropagation();
    }

    const { ctrlKey, metaKey } = evt;

    if (!this.state.showNote) {
      setTimeout(() => this.openDetails({ ctrlKey, metaKey }), 0);
    } else {
      this.openDetails(evt);
    }
  };

  openDetails = (evt) => {
    if (!this.state.showNote) {
      const route = getPropertyDetailsPageUrl(this.props.slug || this.props.id, this.props.locale);
      if (evt.ctrlKey || evt.metaKey) {
        window.open(route);
      } else if (this.props.isMobile) {
        if (this.props.suppressLoading && this.props.setPreloadData) {
          this.props.suppressLoading(true)
            .then(this.props.setPreloadData(this.generatePreloadData()))
            .then(() => browserHistory.push(route));
        } else {
          browserHistory.push(route);
        }
      } else {
        this.openDetailsPopup();
      }
    }
  };

  handleHover = () => {
    if (this.props.onHover) {
      this.props.onHover({ id: this.props.id, city: this.props.cityEn });
    }
  };

  handleEdit = (event) => {
    event.stopPropagation();
    this.props.editProperty(this.props.id);
    const isForeignProperty = checkIsForeignAddress(this.props.addressData);

    setTimeout(() => {
      browserHistory.push(
        appendLocale(
          this.props.locale, `/property/edit?id=${this.props.id}`
        )
      );
    });
  };

  handleShare = () => {
    if (this.props.isMobile) {
      this.props.openModalShare(this.props.id);
    } else {
      this.setState({ isShareTooltipShown: true });
    }
    trackEvent(eventCategory.share, eventAction.click, eventLabel.managePropertiesPage);
  };

  handleSecondJump = () => {
    this.props.openJumpToTopModal();
  };

  handleUpgradePackage = () => {
    this.props.openUpgradePackageModal(this.props.id);
  };

  closeControlsModal = () => this.setState({ isModalControlsOpen: false });
  openControlsModal = () => this.setState({ isModalControlsOpen: true });

  toggleFavorites = (evt) => {
    this.props.toggleFavorite(this.props.id);
    evt.stopPropagation();
    evt.preventDefault(); // way to stop propagation on iPhones
  };

  openDetailsPopup = () => {
    this.props.openDetailsPopup(this.generatePreloadData());
    trackModalOpen(`property/${this.props.id}`);
  };

  generatePreloadData = () => {
    const { price, currency } = this.getResolvedPrice();

    return {
      propertyId: this.props.id,
      slug: this.props.slug,
      address: this.props.addressData,
      price,
      currency,
      additionalInfo: {
        area: this.props.area,
        floor: this.props.floor === 0 ? this.props.groundFloorTitle.toString() : this.props.floor,
        parkingSpots: parkingConverter(this.props.parkingSpots),
        rooms: this.props.rooms
      },
      propertyType: this.props.propertyType,
      optionType: this.props.optionType
    };
  };

  toggleActivity = () => (!this.props.suspended) && this.props.updatePropertyActivity({
    properties: [this.props.property],
    active: !this.props.isActive
  });

  formatShareData = () => {
    const { photo: thumbnail, slug, locale, sharingTitle } = this.props;
    const sharingUrl = makeSharingUrl(`/home-details/${slug}`, locale);

    return {
      sharingUrl,
      sharingTitle,
      thumbnail
    };
  };

  showChart = (action, propertyViews) => {
    const { createdAt } = this.props;
    const publishDate = moment(createdAt).format('DD/MM/YYYY');
    if (!this.props.isMobile) {
      const start = new Date();
      start.setDate(start.getDate() - 10);
      this.props.loadSinglePropertyAnalytics({
        ids: [this.props.id],
        action,
        end: new Date(),
        start
      });
      this.props.openChartOfViews({ publishDate, propertyViews, analyticsAction: action });
    }
  };

  handleExposures = () => this.showChart(analyticsEventAction.VIEW, this.props.propertyViews);
  handlePhone = () => this.showChart(analyticsEventAction.SHOW_NUMBER, this.props.propertyNumber);
  handleFavourites = () => this.showChart(analyticsEventAction.FAVOURITES, this.props.propertyFavorites);

  createControlButtonsRef = (ref) => {
    this.controlButtons = ref;
  };

  clickHandlers = {
    handleShare: this.handleShare,
    handleEdit: this.handleEdit,
    handleSecondJump: this.handleSecondJump,
    openDetailsPopup: this.openDetailsPopup,
    toggleActivity: this.toggleActivity,
    upgradePackage: this.handleUpgradePackage
  };

  toggleAutoJumpActivity = () => {
    const { id, is_active: isActive, is_suspended: isSuspended,
      is_autojump_enabled: autoJumpEnabled } = this.props.property;

    if (isActive && !isSuspended && !this.props.disabledPromotionAction) {
      this.props.updateAutoJumpToTopActivity({
        ids: [id],
        enable: !autoJumpEnabled
      });
    }
  };

  toggleHighlight = () => {
    const { id, is_promoted: isPromoted, is_active: isActive,
      is_suspended: isSuspended } = this.props.property;

    if (isActive && !isSuspended && !this.props.disabledPromotionAction) {
      this.props.updatePropertyHighlighting({
        ids: [id],
        promote: !isPromoted
      });
    }
  };
  toggleTopPromotion = () => {
    const { id, is_top_promoted, is_active, is_suspended, thumbnail } = this.props.property;

    if (thumbnail && is_active && !is_suspended && !this.props.disabledPromotionAction) {
      this.props.updateTopPromotionActivity({
        ids: [id],
        promote: !is_top_promoted
      });
    } else {
      this.props.openMessageModal({ message: (
        <div>{this.props.translate('managePage', 'requiredPromotionTopDescription')}</div>
      ) });
    }
  };

  renderControlButton = ({ props: { className, ...restProps }, icon, title }) => (
    <button
      className={cx(styles.controlButton, className)}
      {...restProps}
    >
      <i className={`icon icon-${icon}`} >
        <i className="path1" />
        <i className="path2" />
        <i className="path3" />
        <i className="path4" />
        <i className="path5" />
        <i className="path6" />
        <i className="path7" />
      </i>
      {!this.props.isMobile && (<br />)}
      <span>{title}</span>
    </button>
  );

  renderControlButtons = () => {
    if (!this.props.editable) {
      return null;
    }

    const {
      isActive,
      isMobile,
      jumpToTopTooltipText,
      copyLinkText,
      canSecondJump,
      suspended,
      promoted,
      autoJumpEnabled
    } = this.props;

    const buttonsConfig = getButtonsConfig(this.props, this.clickHandlers);
    const isForeignProperty = checkIsForeignAddress(this.props.addressData);

    const mobileControls = (
      <div className={cx(styles.propertyButtons, styles.mobileButtons)}>
        {this.renderControlButton(buttonsConfig.share)}
        {!isForeignProperty &&
          this.renderControlButton(buttonsConfig.upgradePackage)
        }

        {!isForeignProperty &&
          <div className={styles.moveToTopButton}>
            {this.renderControlButton(buttonsConfig.jumpToTop)}
            {isActive && (<div className={styles.moveToTopTooltip}>{this.renderAutoJumpDataTitle()} </div>)}
          </div>
        }

        {(canSecondJump) && (this.renderControlButton(buttonsConfig.secondJumpToTop))}
      </div>
    );

    const desktopControls = (
      <div className={styles.propertyButtons}>
        {this.renderControlButton(buttonsConfig.share)}
        {this.renderControlButton(buttonsConfig.edit)}

        {!isForeignProperty &&
          <div className={styles.moveToTopButton}>
            {this.renderControlButton(buttonsConfig.jumpToTop)}
            {isActive && (<div className={styles.moveToTopTooltip}>{this.renderAutoJumpDataTitle()} </div>)}
          </div>
        }

        {this.renderControlButton(buttonsConfig.preview)}
        {this.renderControlButton(buttonsConfig.activity)}
        {!isForeignProperty &&
          this.renderControlButton(buttonsConfig.upgradePackage)
        }

        {(this.state.isShareTooltipShown) && (
          <ShareTooltip
            locale={this.props.locale}
            className="manageProperty"
            sharingData={this.formatShareData()}
            isTooltipShown={this.state.isShareTooltipShown}
            copyLinkTitle={copyLinkText()}
            emailTitle={`${this.props.translate('propertyDetails', 'contacts', 'email')}`}
            hideTooltip={() => this.setState({ isShareTooltipShown: false })}
          />)
        }
      </div>
    );

    return (
      <div className={styles.propertyDesc} ref={this.createControlButtonsRef}>
        {isMobile ? <hr className={styles.buttonDivider} /> : null}
        {isMobile ? mobileControls : desktopControls}
      </div>
    );
  };

  renderControlsModal = () => {
    if (!this.props.isMobile) { return null; }
    const buttonsConfig = getButtonsConfig(this.props, this.clickHandlers);

    return (
      <Modal
        isOpen={this.state.isModalControlsOpen}
        onRequestClose={this.closeControlsModal}
        className="mobileContacts"
        isRtl={this.props.isRtl}
      >
      <div className={styles.mobileMoreControls} onClick={this.closeControlsModal}>
        <div className={styles.controlRow}>
          {this.renderControlButton(buttonsConfig.edit)}
        </div>
        <div className={styles.controlRow}>
          {this.renderControlButton(buttonsConfig.preview)}
        </div>
        <div className={styles.controlRow}>
          {this.renderControlButton(buttonsConfig.activity)}
        </div>
      </div>
      </Modal>
    );
  };

  renderShareModal = () => (this.props.isMobile
    && (this.props.modalShareId === this.props.id)
    && this.props.editable
    && this.props.isActive)
    && (
      <Modal
        isOpen={this.props.isModalShareOpen}
        onRequestClose={this.props.hideModalShare}
        className="mobileContacts"
        isRtl={this.props.isRtl}
      >
        <ShareTooltip
          locale={this.props.locale}
          hideModalShare={this.props.hideModalShare}
          sharingData={this.formatShareData()}
          copyLinkTitle={this.props.copyLinkText()}
          emailTitle={`${this.props.translate('propertyDetails', 'contacts', 'email')}`}
          isMobile
        />
      </Modal>
    );

  renderInactiveOverlay = () => {
    if (this.props.isActive || !this.props.editable) {
      return null;
    }

    return (
      <div className={styles.inactiveOverlay} />
    );
  };

  renderPrice = () => {
    const isProject = this.props.propertyEntityType === ENTITY_TYPES.PROJECT;
    return displayPrice({ ...this.props, address: this.props.addressData }, styles, isProject);
  };

  renderNote = () => ((this.props.propertyNote) ? (
    <div className={styles.noteIconWrapper} ref={(ref) => { this.noteWrapper = ref; }}>
      <img
        src={propertyNoteIcon}
        alt=""
        className={styles.noteIcon}
        onClick={() => {
          this.setState({ showNote: !this.state.showNote });
        }}
      />
      {(this.state.showNote) ? (
        <div className={styles.noteTooltip}>
          <NoteInput
            value={this.props.propertyNote}
            addNote={note => this.props.addPropertyNote(this.props.id, note)}
          />
        </div>
      ) : null}
    </div>
  ) : null);

  renderSuspendedLabel = () => this.props.editable && this.props.suspended && (
    <div className={styles.suspendedLabel}>
      {this.props.suspendedTitle}
      <BottomTooltip
        className="suspendedTooltip"
        msg={(
          <span>
            {this.props.suspendedTooltipMessage}
            <a
              href={`mailto:${supportEmail}`}
              className="link"
              onClick={evt => evt.stopPropagation()}
            > {supportEmail}
            </a>
          </span>
        )}
      />
    </div>
  );

  renderFavoriteIcon = () => (
    <span
      ref={(ref) => { this.favoriteIcon = ref; }}
      className={cx(styles.favoriteIcon, 'favoriteToggler', {
        'icon-favorite-active-with-border': this.props.isFavorite,
        'icon-favorite-disabled': !this.props.isFavorite
      })}
    >
      <span className="path1 favoriteToggler" />
      <span className="path2 favoriteToggler" />
    </span>
  );

  renderLabelNew = () => this.props.isNewProperty && (
    <div className={cx(styles.labelNew, (this.props.className === 'multiPinList') && styles.multiPin)}>
      <OMLabelNew
        label={this.props.newPropertyLabel}
        title={this.props.newPropertyTitle}
        isMobile={this.props.isMobile}
        inMultiPin={this.props.className === 'multiPinList'}
      />
    </div>
  );

  renderLabelTop = () => this.props.isTopPromotedProperty && (
    <div className={styles.labelNew}>
      <OMLabelTop
        label="TOP"
        isMobile={this.props.isMobile}
      />
    </div>
  );

  renderVRIcon = (hasVRLink) => hasVRLink && (
    <span className={cx(styles.labelVR, 'icon', 'icon-vr-view', { [styles.managePropertyVr]: this.props.editable })}>
      <span className="path1" />
      <span className="path2" />
      <span className="path3" />
      <span className="path4" />
      <span className="path5" />
    </span>
  );

  renderMoreControlsButton = () => this.props.editable && this.props.isMobile && (
    <button
      ref={(ref) => { this.moreControlsButton = ref; }}
      className={styles.moreControlsButton}
      onClick={this.openControlsModal}
    >
      <span>&nbsp;• • •&nbsp;</span>
    </button>
  );

  renderClickableArea = () => {
    if (this.props.isMobile || this.props.className !== 'inManageProperties') {
      return null;
    }

    return (
      <div
        ref={(ref) => { this.clickableArea = ref; }}
        className={styles.clickableArea}
        onClick={this.openDetails}
      />
    );
  };

  renderPropertyViews = () => {
    const { openChartOfViews, propertyViews, propertyViewsText, locale, className, isMobile } = this.props;
    if (!this.props.editable) {
      return null;
    }
    const viewsIcon = !isMobile && (
      <i className="icon-views" >
        <span className="path1" />
        <span className="path2" />
        <span className="path3" />
        <span className="path4" />
        <span className="path5" />
        <span className="path6" />
        <span className="path7" />
        <span className="path8" />
        <span className="path9" />
        <span className="path10" />
        <span className="path11" />
      </i>);
    const viewsCount = propertyViews || 0;

    const buttonContent = (locale === 'ru') ?
      [
        { id: 1, value: viewsIcon },
        { id: 2, value: propertyViewsText },
        { id: 3, value: viewsCount }
      ] :
      [
        { id: 1, value: viewsIcon },
        { id: 2, value: viewsCount },
        { id: 3, value: propertyViewsText }
      ];

    return (
      <button className={styles.propertyViews} onClick={this.handleExposures}>
        {buttonContent.map(item => <div key={item.id}>{item.value}</div>)}
      </button>
    );
  };

  renderPhoneClicksCount = () => {
    if (!this.props.editable) {
      return null;
    }

    const phoneClicksCount = this.props.propertyNumber || 0;
    const isForeign = checkIsForeignAddress(this.props.addressData);

    return !isForeign && (
      <div className={styles.phoneClicksCount} onClick={this.handlePhone}>
        <i className="icon icon-phone" />&nbsp;&nbsp;
        <span>{phoneClicksCount}</span>
      </div>
    );
  }

  renderFavouritesCount = () => {
    if (!this.props.editable) {
      return null;
    }

    const favouritesCount = this.props.propertyFavorites || 0;

    return (
      <div className={styles.favouritesCount} onClick={this.handleFavourites}>
        <i className="icon icon-favorite" />&nbsp;&nbsp;
        <span>{favouritesCount}</span>
      </div>
    );
  }

  renderAutoJumpDataTitle = () => {
    const { manual_jump_date: manualJumpDate } = this.props.property;

    if (!!manualJumpDate) {
      return `
        ${this.props.jumpToTopTooltipText}
        ${this.props.translate('managePage', 'manualJumpDate')}
        ${formatTimeFromDate(manualJumpDate)}
      `;
    }

    return this.props.jumpToTopTooltipText;
  }

  renderPromotions = () => {
    const isForeign = checkIsForeignAddress(this.props.addressData);

    if (!this.props.editable || isForeign) {
      return null;
    }

    const { autoJumpEnabled, translate, promoted, isTopPromotedProperty, isCanManagePromotions } = this.props;
    const autoJumpStatus = autoJumpEnabled ? 'Active' : 'Disabled';
    const promotionStatus = promoted ? 'Active' : 'Disabled';
    const topPromotionStatus = isTopPromotedProperty ? 'Active' : 'Disabled';

    const topPromotion = get(this.props, 'userProperties.data.meta.user_properties.top_promotion', null);
    const highlight = get(this.props, 'userProperties.data.meta.user_properties.promotion', null);
    const autoJump = get(this.props, 'userProperties.data.meta.user_properties.jump_promotion', null);

    const promotionUntilTopPromotion = (topPromotion.until && moment(topPromotion.until));
    const promotionUntilTopPromotionFormatted = ((promotionUntilTopPromotion && promotionUntilTopPromotion.utc().format('DD/MM/YYYY')) || '-');

    const promotionUntilHighlight = (highlight.until && moment(highlight.until));
    const promotionUntilHighlightFormatted = ((promotionUntilHighlight && promotionUntilHighlight.utc().format('DD/MM/YYYY')) || '-');

    const promotionUntilAutoJump = (autoJump.until && moment(autoJump.until));
    const promotionUntilAutoJumpFormatted = ((promotionUntilAutoJump && promotionUntilAutoJump.utc().format('DD/MM/YYYY')) || '-');

    const dataTitleTopPromotion = `
      ${topPromotionStatus === 'Active' ? `${translate('managePage', 'validUntil')}: ${promotionUntilTopPromotionFormatted}` : ''}
      ${translate(`topPromotion${topPromotionStatus}RegularUserTitle`).replace('___', `${String.fromCharCode(8207)}TOP`)}
    `;
    const dataTitlePromotionStatus = `
      ${promotionStatus === 'Active' ? `${translate('managePage', 'validUntil')}: ${promotionUntilHighlightFormatted}` : ''}
      ${translate(`highlight${promotionStatus}RegularUserTitle`)}
    `;
    const dataTitleAutoJumpStatus = `
      ${autoJumpStatus === 'Active' ? `${translate('managePage', 'validUntil')}: ${promotionUntilAutoJumpFormatted}` : ''}
      ${translate(`autoJump${autoJumpStatus}RegularUserTitle`)}
    `;

    return (
      <div className={styles.promotionsBlock}>
        <div className={styles.promotionsTitle}>{translate('managePage', 'promotionsTitle')}</div>
        <div>
          <i
            onClick={isCanManagePromotions && this.toggleTopPromotion}
            data-title={dataTitleTopPromotion}
            className={cx(
              `icon-top-promotion${addEnding(topPromotionStatus.toLocaleLowerCase())}`,
              styles.iconTitle
            )}
          >
            <span className="path1" />
            <span className="path2" />
            <span className="path3" />
          </i>
        </div>
        <div>
          <i
            onClick={isCanManagePromotions && this.toggleHighlight}
            data-title={dataTitlePromotionStatus}
            className={cx(
              `icon-promoted${addEnding(promotionStatus.toLocaleLowerCase())}`,
              styles.iconTitle
            )}
          >
            <span className="path1" />
            <span className="path2" />
            <span className="path3" />
          </i>
        </div>
        {/* <div>
          <i
            onClick={isCanManagePromotions && this.toggleAutoJumpActivity}
            data-title={dataTitleAutoJumpStatus}
            className={cx(styles.iconTitle, `icon-auto-jump${addEnding(autoJumpStatus.toLocaleLowerCase())}`)}
          >
            <span className="path1" />
            <span className="path2" />
            <span className="path3" />
          </i>
        </div> */}
      </div>
    );
  };

  render() {
    const { neighborhood, city, address } = this.props;

    const parkingSpots = parkingConverter(this.props.parkingSpots);
    const groundFloor = this.props.floor === 0;
    const favoriteButton = (!this.props.editable && !this.props.hideFavoriteButton) && this.renderFavoriteIcon();
    const isProject = this.props.propertyEntityType === ENTITY_TYPES.PROJECT;
    const videos = get(this.props, 'property.videos', []);
    const isVideoReady = videos.length && isVideoPosterReady(videos[0]);
    const videoPoster = isVideoReady ? buildVideoLinks(videos[0]).thumbnail : undefined;

    // Project images aren't objects with status/error the way property images are - never gate them.
    const propertyImages = get(this.props, 'property.images', []);
    const mediaStatusBadge = resolveMediaStatusBadge({ images: isProject ? [] : propertyImages, videos });
    const firstImage = propertyImages[0];
    const readyPhoto = (isProject || isImagePosterReady(firstImage)) ? this.props.photo : undefined;

    const image = (isProject ? modifyImageLink(readyPhoto) : readyPhoto) || thumbnailPic;
    const { property, locale, translate } = this.props;
    const photoAlt = isProject || !property ? '' : buildCardPhotoAlt({ property, locale, translate });

    return (
      <div
        ref={(cell) => {
          this.propertyCell = cell;
        }}
        vocab="http://schema.org/"
        typeof="Apartment"
        onMouseEnter={this.handleHover}
        className={cx(styles.propertyCell, this.props.className, {
          [styles.defaultCursor]: this.props.editable,
          [styles.promotedProperty]:
            this.props.promoted &&
            !this.props.editable &&
            this.props.className !== 'multiPinList',
          [styles.colorHighlight]: get(this.props, 'property.is_promoted')
        })}
      >
        <div
          className={styles.propertyRow}
          ref={(ref) => {
            this.propertyRow = ref;
          }}
        >
          <div className={styles.iconsWrapper}>
            {this.renderNote()}
            {!this.props.editable && this.renderVRIcon(this.props.hasVRLink)}
            {!isProject && this.renderLabelNew()}
            {!this.props.editable && this.renderLabelTop()}
            {this.renderMoreControlsButton()}
          </div>

          <div
            className={styles.propertyPhoto}
            ref={(ref) => {
              this.propertyPhoto = ref;
            }}
          >
            {!isProject && favoriteButton}
            {this.props.editable && this.renderVRIcon(this.props.hasVRLink)}

            <LazyImage alt={photoAlt} property="photo" src={videoPoster || image} />
            {mediaStatusBadge && mediaStatusBadge.type === 'video' && (
              <div className={styles.mediaStatusBadge}>
                <VideoStatusBadge status={mediaStatusBadge.item.status} error={mediaStatusBadge.item.error} compact />
              </div>
            )}
            {mediaStatusBadge && mediaStatusBadge.type === 'image' && (
              <div className={styles.mediaStatusBadge}>
                <ImageStatusBadge status={mediaStatusBadge.item.status} error={mediaStatusBadge.item.error} compact />
              </div>
            )}
            {this.renderPrice()}
          </div>

          <div className={styles.propertyDescription}>
            {this.props.className === 'multiPinList'
              ? this.renderPrice()
              : null}
            {!isProject && (
              <div className={styles.propertyType}>
                {this.props.editable
                  ? this.props.optionAndPropertyTypes
                  : this.props.propertyType}
              </div>
            )}

            {isProject && (
              <div className={styles.propertyType}>
                {this.props.translate('header', 'projects')}
              </div>
            )}

            <div property="address" style={{ WebkitBoxOrient: 'vertical' }} className={styles.propertyAddress}>
              <span>{address}</span>
              <span className={styles.neighborhood}>
                {neighborhood && (neighborhood !== city) && <span>{neighborhood}, </span>}
                <span>{city}</span>
              </span>
            </div>
            <div
              className={styles.propertyInfo}
              ref={(ref) => {
                this.propertyInfo = ref;
              }}
            >
              {this.props.rooms && (
                <span
                  property="numberOfRooms"
                  className={styles.propertyInfoIconContainer}
                >
                  <i
                    className="icon icon-door"
                    title={this.props.roomsIconTitle}
                  />
                  {this.props.rooms}
                </span>
              )}

              {(this.props.floor || groundFloor) && (
                <span className={styles.propertyInfoIconContainer}>
                  <i className="icon icon-floor" />
                  {groundFloor ? this.props.groundFloorTitle : this.props.floor}
                </span>
              )}

              {this.props.status && (
                <span className={styles.propertyInfoIconContainer}>
                  {`${this.props.translate('status')}: ${this.props.status}`}
                </span>
              )}

              {this.props.area && (
                <span className={styles.propertyInfoIconContainer}>
                  <i
                    className="icon icon-square"
                    title={this.props.squareIconTitle}
                  />
                  {this.props.area}
                </span>
              )}

              {parkingSpots ? (
                <span className={styles.propertyInfoIconContainer}>
                  <i
                    className="icon icon-parkingSpot"
                    title={this.props.parkingIconTitle}
                  />
                  {parkingSpots}
                </span>
              ) : null}

              {/* <span><i className="icon icon-bath" />{this.props.bathrooms}</span> */}
              {/* this.props.parkingSpots ?
              <span><i className="icon icon-parkingSpot" />{parkingConverter(this.props.parkingSpots)}</span> : null */}
            </div>
          </div>
          <div className={styles.controlButtons}>
            <div className={styles.topControlButtons}>
              {this.renderPromotions()}
              <div className={styles.informationWrapper}>
                <div className={styles.information}>
                  {this.renderPhoneClicksCount()}
                  {this.renderFavouritesCount()}
                </div>
                {this.renderPropertyViews()}
              </div>
            </div>
            {this.renderControlButtons()}
          </div>
        </div>

        {this.renderInactiveOverlay()}
        {this.renderClickableArea()}
        {this.renderSuspendedLabel()}
        {this.renderShareModal()}
        {this.renderControlsModal()}
      </div>
    );
  }
}

PropertyCell.propTypes = {
  locale: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
  photo: PropTypes.string,
  price: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired,
  address: PropTypes.string.isRequired,
  rooms: PropTypes.number,
  parkingSpots: PropTypes.object,
  openDetailsPopup: PropTypes.func,
  id: PropTypes.string,
  editButtonText: PropTypes.func,
  openDetailsButtonText: PropTypes.func,
  activeButtonText: PropTypes.func,
  inactiveButtonText: PropTypes.func,
  makeActiveText: PropTypes.func,
  makeInactiveText: PropTypes.func,
  upgradePackageButtonText: PropTypes.func,
  jumpToTopText: PropTypes.func,
  jumpToTopTooltipText: PropTypes.func,
  copyLinkText: PropTypes.func,
  editProperty: PropTypes.func,
  isModalShareOpen: PropTypes.bool,
  hideModalShare: PropTypes.func,
  openModalShare: PropTypes.func,
  openJumpToTopModal: PropTypes.func,
  editable: PropTypes.bool,
  city: PropTypes.string,
  cityEn: PropTypes.string,
  className: PropTypes.string,
  optionAndPropertyTypes: PropTypes.string,
  area: PropTypes.number,
  parkingIconTitle: PropTypes.func,
  roomsIconTitle: PropTypes.func,
  squareIconTitle: PropTypes.func,
  onHover: PropTypes.func,
  isActive: PropTypes.bool,
  updatePropertyActivity: PropTypes.func,
  movePropertyToTop: PropTypes.func,
  canMoveToTop: PropTypes.bool,
  canSecondJump: PropTypes.bool,
  floor: PropTypes.number,
  groundFloorTitle: PropTypes.func,
  addressData: PropTypes.object,
  propertyType: PropTypes.string,
  propertyNote: PropTypes.string,
  addPropertyNote: PropTypes.func,
  isFavorite: PropTypes.bool,
  toggleFavorite: PropTypes.func,
  isRtl: PropTypes.bool,
  promoted: PropTypes.bool,
  promotedTitle: PropTypes.string,
  suspended: PropTypes.bool,
  suspendedTitle: PropTypes.string,
  suspendedTooltipMessage: PropTypes.string,
  hideFavoriteButton: PropTypes.bool,
  sharingTitle: PropTypes.string,
  shareButtonText: PropTypes.func,
  suppressLoading: PropTypes.func,
  setPreloadData: PropTypes.func,
  optionType: PropTypes.string,
  isNewProperty: PropTypes.bool,
  hasVRLink: PropTypes.bool,
  newPropertyLabel: PropTypes.string,
  newPropertyTitle: PropTypes.string,
  autoDeactivatedProperty: PropTypes.string,
  openChartOfViews: PropTypes.func,
  propertyViews: PropTypes.number,
  propertyFavorites: PropTypes.number,
  propertyNumber: PropTypes.number,
  loadSinglePropertyAnalytics: PropTypes.func,
  propertyViewsText: PropTypes.func,
  createdAt: PropTypes.string,
  modalShareId: PropTypes.string,
  autoJumpEnabled: PropTypes.bool,
  isTopPromotedProperty: PropTypes.bool,
  translate: PropTypes.func,
  openUpgradePackageModal: PropTypes.func,
  propertyEntityType: PropTypes.string,
  status: PropTypes.string,
  searchOption: PropTypes.string,
  neighborhood: PropTypes.string,
  property: PropTypes.object,
  userProperties: PropTypes.object,
  updateAutoJumpToTopActivity: PropTypes.func,
  updatePropertyHighlighting: PropTypes.func,
  updateTopPromotionActivity: PropTypes.func,
  disabledPromotionAction: PropTypes.bool,
  isCanManagePromotions: PropTypes.bool,
  openMessageModal: PropTypes.func,
  slug: PropTypes.string,
  currency: PropTypes.string,
  prices: PropTypes.object,
  overrideCurrency: PropTypes.string
};

export default PropertyCell;
